import { memo, useCallback } from 'react';

import { DateInput } from '@air/primitive-date-input';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@air/primitive-select';

import { type DateCustomFieldOperator } from '~/store/filters/types';
import {
  DATE_CUSTOM_FIELD_OPERATOR_LABELS,
  DATE_CUSTOM_FIELD_OPERATORS,
  isDateCustomFieldOperator,
} from '~/utils/filters/dateCustomFieldFilter';

export const DATE_CUSTOM_FIELD_FILTER_OPERATOR = 'DATE_CUSTOM_FIELD_FILTER_OPERATOR';
export const DATE_CUSTOM_FIELD_FILTER_START = 'DATE_CUSTOM_FIELD_FILTER_START';
export const DATE_CUSTOM_FIELD_FILTER_END = 'DATE_CUSTOM_FIELD_FILTER_END';

const OPERATOR_TEST_IDS: Record<DateCustomFieldOperator, string> = {
  range: 'DATE_CUSTOM_FIELD_FILTER_OPERATOR_RANGE',
  notSet: 'DATE_CUSTOM_FIELD_FILTER_OPERATOR_NOT_SET',
};

export type DateCustomFieldFilterProps = {
  fieldName: string;
  operator: DateCustomFieldOperator;
  startDate: Date | undefined;
  endDate: Date | undefined;
  onOperatorChange: (operator: DateCustomFieldOperator) => void;
  onStartDateChange: (date: Date | [Date, Date] | null) => void;
  onEndDateChange: (date: Date | [Date, Date] | null) => void;
};

export const DateCustomFieldFilter = memo(
  ({
    fieldName,
    operator,
    startDate,
    endDate,
    onOperatorChange,
    onStartDateChange,
    onEndDateChange,
  }: DateCustomFieldFilterProps) => {
    const handleOperatorValueChange = useCallback(
      (value: string) => {
        if (isDateCustomFieldOperator(value)) {
          onOperatorChange(value);
        }
      },
      [onOperatorChange],
    );

    return (
      <div className="flex flex-col items-start gap-2">
        <Select onValueChange={handleOperatorValueChange} value={operator}>
          <SelectTrigger
            aria-label={`${fieldName} operator`}
            className="w-auto border-0 font-medium"
            data-testid={DATE_CUSTOM_FIELD_FILTER_OPERATOR}
            placeholder="Select operator"
            size="small"
          />
          <SelectContent className="min-w-[304px]" sideOffset={4}>
            {DATE_CUSTOM_FIELD_OPERATORS.map((option) => (
              <SelectItem data-testid={OPERATOR_TEST_IDS[option]} key={option} value={option}>
                {DATE_CUSTOM_FIELD_OPERATOR_LABELS[option]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {operator === 'range' ? (
          <div className="flex items-center">
            <DateInput
              className="min-w-40"
              endDate={endDate}
              maxDate={endDate}
              name="startDate"
              onChange={onStartDateChange}
              selected={startDate}
              size="medium"
              startDate={startDate}
              testId={DATE_CUSTOM_FIELD_FILTER_START}
            />
            <div className="mx-2.5 h-0.5 w-3 rounded-2 ads:bg-ads-gray-9 bg-gray-9" />
            <DateInput
              className="min-w-40"
              endDate={endDate}
              minDate={startDate}
              name="endDate"
              onChange={onEndDateChange}
              selected={endDate}
              selectsEnd
              size="medium"
              startDate={startDate}
              testId={DATE_CUSTOM_FIELD_FILTER_END}
            />
          </div>
        ) : null}
      </div>
    );
  },
);

DateCustomFieldFilter.displayName = 'DateCustomFieldFilter';
